/**
 * autoContext - Collects browser/device/user context for feedback submissions.
 *
 * Gathers page URL, route, user identity, organisation, active business, browser
 * user-agent, viewport dimensions, and browser language from the runtime environment.
 * Callers pass the application state they already hold; this module only reads globals
 * (window, navigator) for environment data.
 *
 * @module services/autoContext
 */

/**
 * Collect automatic context for a feedback submission.
 *
 * Reads window.location, navigator, and the caller-supplied application state
 * objects to build a structured context payload that the backend stores in
 * the `browser_info` JSONB column.
 *
 * @param {Object} options - Application state to include.
 * @param {Object|null} options.user - Currently authenticated user object (id, email, role).
 * @param {Object|null} options.activeOrg - Active organisation (id field used).
 * @param {Object|null} options.activeBusiness - Active business (id, name fields used).
 * @returns {Object} Context object with `page`, `user`, and `browser` sub-objects.
 *
 * @example
 * const ctx = getAutoContext({ user, activeOrg, activeBusiness });
 * // ctx.page.url  → 'https://app.cogslog.com/invoices'
 * // ctx.user.email → 'mark@cogslog.com'
 * // ctx.browser.viewport → '1440x900'
 */
function getAutoContext({ user = null, activeOrg = null, activeBusiness = null } = {}) {
  const pageUrl = (typeof window !== 'undefined' && window.location)
    ? window.location.href
    : '';

  const route = (typeof window !== 'undefined' && window.location)
    ? window.location.pathname
    : '';

  const userAgent = (typeof navigator !== 'undefined')
    ? (navigator.userAgent || '')
    : '';

  const language = (typeof navigator !== 'undefined')
    ? (navigator.language || '')
    : '';

  const viewport = (typeof window !== 'undefined')
    ? `${window.innerWidth}x${window.innerHeight}`
    : '';

  return {
    page: {
      url: pageUrl,
      route,
      business_id: activeBusiness ? (activeBusiness.id ?? null) : null,
      business_name: activeBusiness ? (activeBusiness.name ?? null) : null,
    },
    user: {
      id: user ? (user.id ?? null) : null,
      email: user ? (user.email ?? null) : null,
      org_id: activeOrg ? (activeOrg.id ?? null) : null,
      role: user ? (user.role ?? null) : null,
    },
    browser: {
      user_agent: userAgent,
      viewport,
      language,
    },
  };
}

export { getAutoContext };
